import { Injectable, } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { APIService, KPI } from '../api';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable()
export class AuditsService {
  public loading$ = new BehaviorSubject<boolean>(false);

  private _audits$: Observable<KPI.Audit[]>;
  private auditRefresh$ = new BehaviorSubject<void>(null);

  constructor(private api: APIService) {}

  /**
   * Recover list of audits
   */
  public get audits$(): Observable<KPI.Audit[]> {
    if (!this._audits$) {
      this._audits$ = this.auditRefresh$.pipe(
        switchMap(() =>
          this.api.audit_audits().pipe(tap(() => this.loading$.next(false))),
        ),
        shareReplay(1),
      );
    }

    return this._audits$;
  }

  // Audit.NotationState
  notationStates = toSignal(this.api.audit_api_audit_notation_states(), { initialValue: [] });

  /**
   * Forces refresh of audits
   */
  public refreshAudits(): void {
    this.loading$.next(true);
    this.auditRefresh$.next();
  }

  /**
   * Creates an audit using the api
   *
   * @param audit
   */
  public postAudit(audit: Partial<KPI.Audit>): Observable<KPI.Audit> {
    this.loading$.next(true);
    return this.api
      .audit_audits_post(audit)
      .pipe(tap(() => this.refreshAudits()));
  }

  /**
   * Patch an audit using the api
   *
   * @param audit
   */
  public patchAudit(
    auditId: number,
    audit: Partial<KPI.Audit>,
  ): Observable<KPI.Audit> {
    this.loading$.next(true);
    return this.api
      .audit_audits_patch(auditId, audit)
      .pipe(tap(() => this.refreshAudits()));
  }

  /**
   * Get all kpi expert defaults for a perimeter
   *
   * @param perimeter
   */
  public getDefaults(perimeter: number): Observable<any> {
    return this.api
      .audit_kpi_expert_defaults(perimeter)
      .pipe(map((sections) => sections.sort((a, b) => a.order - b.order)));
  }
}
